import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { formatDateTime } from 'helpers/formaters';
import { errorMessage, isEndpointMissing } from 'common/packages';

const AccessPanel = (props) => {
  const { application, pkg, rows, loading, error, busy, canManage, onAdd, onRevoke } = props;

  // A public package evaluates no access at all, so a list here would describe a
  // check that never runs. The caller gates this too; this is the backstop.
  if (pkg.is_public) return null;

  const inert = rows.filter((row) => row.is_licensed === false);

  return (
    <Card>
      <CardHeader
        title="Account access"
        subheader="This package is not public, so only the accounts below can download it, its thumbnail, its icon or its manifest — and only while they hold a licence for this application."
        action={
          canManage ? (
            <Button
              startIcon={<PersonAddIcon />}
              onClick={onAdd}
              disabled={busy || Boolean(error)}
            >
              Add account
            </Button>
          ) : null
        }
      />
      <Divider />
      {loading && <LinearProgress />}
      <CardContent>
        <Stack spacing={2}>
          {!canManage && !error && !loading && (
            <Alert severity="info">
              Read-only: the API did not offer an access link for this package, so grants
              cannot be added or removed from here.
            </Alert>
          )}

          {error &&
            (isEndpointMissing(error) ? (
              <Alert severity="info">
                The access list is unavailable: this environment's API does not serve the
                package access endpoints yet. Nothing is broken here, and no grants have
                been lost — the list will appear once api-accounts ships them.
              </Alert>
            ) : (
              <Alert severity="error">{errorMessage(error)}</Alert>
            ))}

          {!error && !loading && rows.length === 0 && (
            <Alert severity="warning">
              Nobody can download this package yet. It is not public and no account has
              been granted access, so every download, thumbnail, icon and manifest
              request will be refused — including your own.
            </Alert>
          )}

          {inert.length > 0 && (
            <Alert severity="warning">
              {inert.length === 1 ? 'One account is' : `${inert.length} accounts are`} granted
              but hold no licence for <strong>{application}</strong>, so the grant gives them
              nothing: a download needs the licence and the grant. Add the licence to the
              account — it cannot be granted from this screen.
            </Alert>
          )}

          {!error && rows.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Licence</TableCell>
                  <TableCell>Granted</TableCell>
                  {canManage && <TableCell align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.uuid || row.account_uuid} hover>
                    <TableCell>
                      {row.account_name || '—'}
                      <Typography variant="body2" color="text.secondary">
                        {row.account_uuid}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {row.account_email || '—'}
                    </TableCell>
                    <TableCell>
                      {row.is_licensed === false ? (
                        <Chip size="small" color="warning" label="Inert — no licence" />
                      ) : (
                        <Chip size="small" variant="outlined" label="Licensed" />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.created_at) || '—'}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          disabled={busy}
                          onClick={() => onRevoke(row)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

AccessPanel.propTypes = {
  application: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  canManage: PropTypes.bool,
  error: PropTypes.object,
  loading: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  pkg: PropTypes.object.isRequired,
  rows: PropTypes.array.isRequired,
};

export default AccessPanel;
